import { useCallback, useState } from 'react';
import { TargetCatalogEntryInfo } from 'ais-facade-api/api';
import { Button, Dropdown } from '..';
import { useAddToFleetMutation } from 'app/api/fleet';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { useGetVesselCatalogMutation, useLazyGetHistoryQuery } from 'app/api/facade';
import { Link, useNavigate } from 'react-router-dom';
import { DropDownItem } from '../Dropdown';

import { useTranslation } from 'react-i18next';
import { setFindAton, setFindVessel } from 'app/utils/selectedTargetsSlice';

interface Props {
  vessel: TargetCatalogEntryInfo;
}

export const VesselActionCell = ({ vessel }: Props) => {
  const { t } = useTranslation();

  const user = useAppSelector((state) => state.userSlice.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [dropdown, setDropdown] = useState(false);
  const [, catalogRequest] = useGetVesselCatalogMutation({
    fixedCacheKey: 'get-vessels-mutation',
  });

  const canBeShown = typeof vessel?.position?.latitude === 'number' && typeof vessel.position.longitude === 'number';

  const [addToFleet] = useAddToFleetMutation();
  const [downloadHistory] = useLazyGetHistoryQuery();

  const handleDownload = useCallback(() => {
    if (typeof vessel?.mmsi === 'number') {
      downloadHistory(vessel?.mmsi)
        .unwrap()
        .then(({ url, filename }) => {
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);

          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        });
    }
  }, [vessel]);

  const handleAddToFleet = () => {
    const user_id = user?.value.sub;

    if (user_id && typeof vessel?.mmsi === 'number') {
      addToFleet({ mmsiArray: [vessel.mmsi] })
        .unwrap()
        .then(() => {
          catalogRequest.reset();
        });
    }

    setDropdown(false);
  }

  const showVessel = () => {
    const isAton = vessel?.registryData?.specialType && vessel.registryData.specialType === 'ATON';
    if (isAton) {
      dispatch(setFindAton(vessel));
    } else dispatch(setFindVessel(vessel));
    navigate('/mapview');
  }

  return (
    <>
      <Button
        variant='icon-only'
        iconName='more-icon'
        iconSize="13px"
        aria-expanded={dropdown}
        onClick={() => setDropdown((prev) => !prev)}
      />

      <Dropdown
        dropdown={dropdown}
        onClickOutside={() => {
          setDropdown(false);
        }}
      >
        <DropDownItem>
          <button
            aria-disabled={!canBeShown}
            disabled={!canBeShown}
            onClick={showVessel}
          >
            {t('ShowOnMap')}
          </button>
        </DropDownItem>

        <DropDownItem>
          <Link to={`/vessels/${vessel.mmsi}`}>{t('VesselInfo')}</Link>
        </DropDownItem>

        <DropDownItem>
          <button
            aria-disabled={vessel.belongsToFleet}
            disabled={vessel.belongsToFleet}
            onClick={handleAddToFleet}
          >
            {t('ButtonLabelAddVesselToFleet')}
          </button>
        </DropDownItem>

        <DropDownItem>
          <button
            aria-disabled={!handleDownload}
            disabled={!handleDownload}
            onClick={handleDownload}
          >
            {t('ButtonLabelDownloadHistory')}
          </button>
        </DropDownItem>
      </Dropdown>
    </>
  );
};
